import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Skeleton,
    Box
  } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { parseProductInfo } from "../../../utils/parseProductInfo.js";

/**
 * table style
 */
  const StyledTable = styled(Table)(() => ({
    width: 1560,
    borderCollapse: "separate",
    borderSpacing: 0,
    overflowX: 'hidden'
  }));

  const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",  
    height: "50px",
    fontSize: "10px",
    textAlign: "center", 
    borderBottom: "2px solid #ddd",
    borderTop: "2px solid #ddd",
    position: "sticky",
    top: 0,
    zIndex: 1,
    // 오른쪽 세로 구분선
    "&::after": {
      content: '""',
      position: "absolute",
      top: "25%",     // 세로선 길이 조절
      bottom: "25%",  // 세로선 길이 조절
      right: 0,
      width: "1.5px",
      backgroundColor: "#ddd",
    },
    "&:last-of-type::after": {
      content: "none", // 마지막 셀엔 선 없음
    },
    padding: 0 
  }));

  const StyledRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
      backgroundColor: "#f0f0f0",
    }
  }));

  const StyledCell = styled(TableCell)(() => ({
    borderBottom: "1px solid #e0e0e0",
    fontSize: "9px",
    padding: 0 
  }));


/**
 * 마우 오버 이미지 툴팁 시 레이지 로딩 처리
 * @param {*} param0 
 * @returns 
 */  
function LazyImageTooltip({ imageUrl, children }) {
  const [loaded, setLoaded] = useState(false);        // 이미지 로딩 완료 여부
  const [visible, setVisible] = useState(false);      // 컴포넌트가 뷰포트에 들어왔는지
  const containerRef = useRef(null);                  // 관찰 대상 ref

  // 컴포넌트가 화면에 들어오면 visible = true
  useEffect(() => {
    const node = containerRef.current; // 👈 ref를 로컬 변수로 저장
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
  
    if (node) {
      observer.observe(node);
    }
  
    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Tooltip
        title={
          <Box
            sx={{
              width: 150,
              height: 150,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!loaded && <Skeleton variant="rectangular" width={150} height={150} />}
            {visible && (
              <img
                src={imageUrl}
                alt="미리보기"
                style={{
                  display: loaded ? "block" : "none",
                  width: 150,
                  height: "auto",
                  borderRadius: 8,
                }}
                onLoad={() => setLoaded(true)}
              />
            )}
          </Box>
        }
        arrow
        placement="right"
      >
        {children}
      </Tooltip>
    </div>
  );
}

/**
 * 공통 랜더 함수
 * @param {*} param0 
 * @returns 
 */
const renderChannelData = (title, price, pageUrl, imageUrl) => (
  <>
    <StyledCell sx={{ textAlign: "center", width: "65px", padding: "5px 0px 5px 5px" }}>
      {imageUrl?.startsWith("http") ? (
        <LazyImageTooltip imageUrl={imageUrl}>
          <Typography
            sx={{ cursor: "pointer", m: 0, lineHeight: 1.2 }}
            onClick={() => window.open(pageUrl, "_blank")}
          >
            <img
              src={imageUrl}
              alt="product"
              loading="lazy"
              style={{ width: 60, height: 60, borderRadius: 6 }}
            />
          </Typography>
        </LazyImageTooltip>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      )}
    </StyledCell>
    <StyledCell sx={{ width: "157.5px", padding: "0px 0px 0px 5px" }}>
      <Typography sx={{ whiteSpace: "pre-line", fontSize: "9px", m: 0, lineHeight: 1.2 }}>
        {title}
      </Typography>
    </StyledCell>
    <StyledCell sx={{ width: "100px", padding: "0px 0px 0px 5px" }}>
      <Typography sx={{ whiteSpace: "pre-line", fontSize: "9px", m: 0, lineHeight: 1.2 }}>
        {price}
      </Typography>
    </StyledCell>
  </>
);

export default function RealtimeProductTable({products, selectedProducts, onToggle, isLoading}) {
  const skeletonRows = Array.from({ length: 13 });
  return (
    <StyledTable stickyHeader size="small">
      <TableHead sx={{ backgroundColor: "#fafafa" }}>
        <TableRow>
          <StyledHeaderCell sx={{width: 30, padding: 0 }}>번<br/>호</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 75, padding: 0 }}>카테고리</StyledHeaderCell> 
          <StyledHeaderCell sx={{width: 322.5, padding: 0 }} colSpan={3}>올리브영</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 322.5, padding: 0 }} colSpan={3}>무신사</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 322.5, padding: 0 }} colSpan={3}>화해</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 322.5, padding: 0 }} colSpan={3}>시코르</StyledHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
      {
      isLoading ? skeletonRows.map((_, i) => (
          <StyledRow key={i}>
            {Array.from({ length: 14 }).map((_, j) => (
              <StyledCell key={j}>
                <Skeleton variant="text" width="90%" height={70} />
              </StyledCell>
            ))}
          </StyledRow>
        ))
      : products.length === 0 ? (
        <StyledRow>
          <StyledCell colSpan={14} align="center" height="705px">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              opacity={0.5}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 40,
                }}
              >
                😔
              </Box>
              <Typography variant="body2" color="text.secondary" mt={1}>
                아쉽지만, 조건에 맞는 결과가 없습니다.
              </Typography>
            </Box>
          </StyledCell>
        </StyledRow>
      ) : (
        products.map((product, index) => {
          const channels = ["Oliveyoung", "Musinsa", "Hwahae", "Chicor"];
          const parsedData = channels.map((channel) => ({
            channel,
            ...(() => {
              const [title, price, pageUrl, imageUrl] = parseProductInfo(product[channel]);
              return { title, price, pageUrl, imageUrl };
            })(),
          }));
        
          return (
            <StyledRow key={product.name}>
              <StyledCell sx={{ textAlign: "center", padding: 0 }}>{index + 1}</StyledCell>
              <StyledCell sx={{ textAlign: "center", padding: 0 }}>
                {product.CategoryType}
                <br />
                {product.Category}
              </StyledCell>
        
              {parsedData.map(({ title, price, pageUrl, imageUrl }) =>
                renderChannelData(title, price, pageUrl, imageUrl)
              )}
            </StyledRow>
          );
        })
      )}
      </TableBody>
    </StyledTable>
  );
}
